import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import * as agentApi from '@/lib/agents/api';
import type { Crew, CrewProcess } from '@/lib/agents/types';
import { CREW_PROCESS_INFO } from '@/lib/agents/types';
import {
  Plus,
  Search,
  Users,
  MoreVertical,
  Play,
  Edit,
  Trash2,
  Loader2,
  ArrowRight,
  GitBranch,
  GitMerge,
  MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_dashboard/crews/')({
  component: CrewsPage,
});

// Icon mapping for process types
const PROCESS_ICONS: Record<CrewProcess, React.ElementType> = {
  sequential: ArrowRight,
  hierarchical: GitBranch,
  parallel: GitMerge,
  consensual: MessageCircle,
};

function CrewsPage() {
  const [loading, setLoading] = useState(true);
  const [crews, setCrews] = useState<Crew[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    async function fetchCrews() {
      try {
        const response = await agentApi.listCrews({
          search: searchQuery || undefined,
        });
        setCrews(response.data);
      } catch (error) {
        console.warn('Failed to fetch crews:', error);
        setCrews([]);
      } finally {
        setLoading(false);
      }
    }

    fetchCrews();
  }, [searchQuery]);

  const handleDelete = async (crewId: string, crewName: string) => {
    if (!confirm(`Are you sure you want to delete "${crewName}"?`)) return;

    try {
      await agentApi.deleteCrew(crewId);
      setCrews(crews.filter((c) => c.id !== crewId));
      toast.success('Crew deleted');
    } catch {
      toast.error('Failed to delete crew');
    }
  };

  return (
    <div className="container py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Multi-Agent Crews</h1>
          <p className="text-muted-foreground">
            Build teams of specialized agents that collaborate on complex tasks
          </p>
        </div>
        <Button asChild>
          <Link to="/crews/create">
            <Plus className="mr-2 h-4 w-4" />
            New Crew
          </Link>
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search crews..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Crews Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : crews.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium mb-2">No crews yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create a multi-agent crew to tackle complex tasks collaboratively
            </p>
            <Button asChild>
              <Link to="/crews/create">
                <Plus className="mr-2 h-4 w-4" />
                Create Your First Crew
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {crews.map((crew) => {
            const ProcessIcon = PROCESS_ICONS[crew.process_type];
            return (
              <Card key={crew.id} className="hover:border-primary/50 transition-colors">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{crew.name}</CardTitle>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <ProcessIcon className="h-3 w-3" />
                          {CREW_PROCESS_INFO[crew.process_type].label}
                        </div>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/crews/${crew.id}` as any}>
                            <Play className="mr-2 h-4 w-4" />
                            Run Crew
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/crews/${crew.id}/edit` as any}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => handleDelete(crew.id, crew.name)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="line-clamp-2 mb-3">
                    {crew.description || 'No description'}
                  </CardDescription>

                  {/* Members */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs text-muted-foreground">
                      {crew.members.length} agent{crew.members.length !== 1 ? 's' : ''}
                    </span>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-xs text-muted-foreground">
                      {crew.tasks.length} task{crew.tasks.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Member badges */}
                  <div className="flex flex-wrap gap-1">
                    {crew.members.slice(0, 3).map((member) => (
                      <Badge key={member.id} variant="secondary" className="text-xs">
                        {member.name}
                      </Badge>
                    ))}
                    {crew.members.length > 3 && (
                      <Badge variant="secondary" className="text-xs">
                        +{crew.members.length - 3}
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <span className="text-xs text-muted-foreground">
                      Updated {new Date(crew.updated_at).toLocaleDateString()}
                    </span>
                    <Button size="sm" variant="ghost" asChild>
                      <Link to={`/crews/${crew.id}` as any}>
                        Run
                        <ArrowRight className="ml-1 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
